import React from 'react';
import { avatarColor, avatarInitials } from './avatarUtils';

/**
 * Smart relative timestamp:
 *   today        → "2:30 PM"
 *   this week    → "Mon 2:30 PM"
 *   older        → "Mar 15"  (or "Mar 15, 2024" if different year)
 */
function formatTimestamp(date) {
  if (!date) return '';

  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  if (diffDays === 0) return timeStr;

  if (diffDays < 7) {
    const day = date.toLocaleDateString([], { weekday: 'short' });
    return `${day} ${timeStr}`;
  }

  const sameYear = date.getFullYear() === now.getFullYear();
  if (sameYear) {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

const safeParseDate = (value) => {
  if (!value) return null;
  try {
    if (value.toDate && typeof value.toDate === 'function') return value.toDate();
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
};

// ── Component ─────────────────────────────────────────────────────────────────

const ConversationItem = ({ conversation, selected, onSelect }) => {
  const timestamp   = safeParseDate(conversation.lastMessageTime);
  const isUnread    = (conversation.unreadCount || 0) > 0;
  const isMentioned = conversation._signals?.mentioned;

  const displayName = conversation.displayName || conversation.clientName || conversation.clientPhone || '';
  const avatarBg    = avatarColor(displayName || conversation.id || '?');
  const initials    = avatarInitials(displayName);

  // Row background + hover states:
  //   selected   → brand-tinted stripe (unchanged)
  //   unread     → subtle blue tint so it pops without overwhelming
  //   read       → clean white with light hover
  const rowStateClasses = selected
    ? 'bg-[#0f4c81]/8 border-l-[3px] border-l-[#0f4c81]'
    : isUnread
      ? 'bg-blue-50/60 hover:bg-blue-50 active:bg-blue-100'
      : 'bg-white hover:bg-gray-50 active:bg-gray-100';

  return (
    <div
      onClick={() => onSelect(conversation)}
      data-testid={`conversation-item-${conversation.id}`}
      className={`
        group px-4 py-4 lg:py-3 border-b border-gray-100 lg:border-gray-200
        cursor-pointer select-none transition-colors duration-100
        ${rowStateClasses}
      `}
    >
      <div className="flex items-center gap-3.5 lg:gap-3 min-w-0">

        {/* Avatar — bumped to 44px on mobile for proper tap target + breathing room */}
        <div className="relative flex-shrink-0">
          <div
            className="w-11 h-11 lg:w-10 lg:h-10 rounded-full flex items-center justify-center text-white text-[15px] lg:text-sm font-semibold shadow-sm"
            style={{ backgroundColor: avatarBg }}
          >
            {initials}
          </div>
          {isUnread && (
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-blue-500 border-2 border-white" />
          )}
        </div>

        {/* Text content */}
        <div className="flex-1 min-w-0">

          {/* Row 1: name + timestamp */}
          <div className="flex items-baseline justify-between gap-2 mb-1 lg:mb-0.5">
            <span
              className={`
                text-[16px] lg:text-sm truncate
                ${isUnread
                  ? 'font-bold text-gray-900'
                  : 'font-semibold text-gray-900 lg:font-medium lg:text-gray-800'}
              `}
            >
              {isMentioned && (
                <span className="text-indigo-500 mr-0.5 text-[11px] font-bold">@</span>
              )}
              {displayName || <span className="italic text-gray-400">Unknown</span>}
            </span>
            <span className="text-[11px] font-normal text-gray-400 flex-shrink-0 tabular-nums">
              {formatTimestamp(timestamp)}
            </span>
          </div>

          {/* Row 2: direction arrow + last message preview
              Preview intentionally stays muted + regular-weight even when unread —
              we want the NAME to carry the "priority queue" signal, not the preview. */}
          <p className="text-[13px] lg:text-xs truncate leading-snug flex items-center gap-1 text-gray-500">
            {conversation.lastMessageFrom === 'client' && (
              <span className="text-[10px] text-gray-400 flex-shrink-0">←</span>
            )}
            {conversation.lastMessageFrom === 'staff' && (
              <span className="text-[10px] text-blue-400 flex-shrink-0">→</span>
            )}
            <span className="truncate">
              {conversation.lastMessage || <span className="italic">No messages yet</span>}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConversationItem;
